<script lang="ts">
	import { reveal } from '$lib/actions/reveal';
	import SectionHeading from '$lib/components/ui/SectionHeading.svelte';

	const stats = [
		{ label: 'Multi-region', sub: 'Control plane' },
		{ label: 'Outbound only', sub: 'Zero firewall changes' },
		{ label: 'Multiple platforms', sub: 'From high-performance x86 to low-cost ARM' },
		{ label: 'Offline-tolerant', sub: 'Designed for spotty connections' }
	];

	const edgeNodes = [
		{ cx: 80, cy: 80, r: 8, delay: '0s', primary: true },
		{ cx: 320, cy: 80, r: 8, delay: '0.4s', primary: true },
		{ cx: 80, cy: 320, r: 8, delay: '0.8s', primary: true },
		{ cx: 320, cy: 320, r: 8, delay: '1.2s', primary: true },
		{ cx: 60, cy: 200, r: 6, primary: false },
		{ cx: 340, cy: 200, r: 6, primary: false },
		{ cx: 200, cy: 60, r: 6, primary: false },
		{ cx: 200, cy: 340, r: 6, primary: false }
	];

	const lines = edgeNodes.map((n) => ({ x1: 200, y1: 200, x2: n.cx, y2: n.cy }));

	const packets = [
		{ path: 'M 200 200 L 80 80', delay: '0s' },
		{ path: 'M 200 200 L 320 320', delay: '0.5s' },
		{ path: 'M 320 80 L 200 200', delay: '1s' },
		{ path: 'M 80 320 L 200 200', delay: '1.5s' }
	];
</script>

<section class="mx-auto mt-20 w-full max-w-[1216px] px-4 sm:mt-32 lg:mt-40 lg:px-0">
	<div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
		<div class="reveal" use:reveal>
			<SectionHeading
				eyebrow="Architecture"
				title="Distributed by design"
				titleHtml="Distributed by design. Deploy anywhere."
				description="The Admiral control plane runs across multiple regions with automatic failover. Your devices connect out over encrypted tunnels, so there are no inbound firewall rules to open and no ports to expose."
				align="left"
			/>
			<div class="mt-10 grid grid-cols-2 gap-6">
				{#each stats as stat}
					<div>
						<div class="font-display text-xl font-medium tracking-tight text-white">
							{stat.label}
						</div>
						<div class="mt-1 font-display text-sm text-white/50">{stat.sub}</div>
					</div>
				{/each}
			</div>
		</div>

		<div class="reveal reveal-delay-2" use:reveal>
			<div class="network-viz relative aspect-square overflow-hidden rounded-2xl p-6">
				<div class="subtle-glow absolute inset-0 -z-10"></div>

				<svg viewBox="0 0 400 400" class="h-full w-full" xmlns="http://www.w3.org/2000/svg">
					<defs>
						<radialGradient id="centerGrad">
							<stop offset="0%" stop-color="#2156E9" stop-opacity="0.8" />
							<stop offset="100%" stop-color="#2156E9" stop-opacity="0" />
						</radialGradient>
					</defs>

					<g class="network-lines" fill="none">
						{#each lines as line}
							<line
								x1={line.x1}
								y1={line.y1}
								x2={line.x2}
								y2={line.y2}
								stroke="rgba(33,86,233,0.3)"
								stroke-width="1"
								stroke-dasharray="4 4"
							/>
						{/each}
					</g>

					<circle cx="200" cy="200" r="40" fill="url(#centerGrad)" opacity="0.3" />

					<circle cx="200" cy="200" r="20" fill="none" stroke="#2156E9" opacity="0.5">
						<animate attributeName="r" from="20" to="50" dur="3s" repeatCount="indefinite" />
						<animate attributeName="opacity" from="0.5" to="0" dur="3s" repeatCount="indefinite" />
					</circle>

					<circle cx="200" cy="200" r="20" fill="#2156E9" />
					<text
						x="200"
						y="205"
						text-anchor="middle"
						fill="white"
						font-size="9"
						font-family="ui-monospace, SF Mono, Menlo, monospace"
						font-weight="600"
					>
						CORE
					</text>

					{#each edgeNodes as node}
						<circle
							cx={node.cx}
							cy={node.cy}
							r={node.r}
							fill="#2156E9"
							opacity={node.primary ? 0.8 : 0.6}
						/>
						{#if node.primary && node.delay}
							<circle cx={node.cx} cy={node.cy} r={node.r} fill="none" stroke="#2156E9">
								<animate
									attributeName="r"
									from={node.r}
									to={node.r + 12}
									dur="2s"
									begin={node.delay}
									repeatCount="indefinite"
								/>
								<animate
									attributeName="opacity"
									from="1"
									to="0"
									dur="2s"
									begin={node.delay}
									repeatCount="indefinite"
								/>
							</circle>
						{/if}
					{/each}

					{#each packets as packet}
						<circle r="3" fill="#8AA7F5">
							<animateMotion
								dur="3s"
								repeatCount="indefinite"
								begin={packet.delay}
								path={packet.path}
							/>
						</circle>
					{/each}
				</svg>
			</div>
		</div>
	</div>
</section>

<style>
	.network-viz {
		position: relative;
		background: #0a0b14;
	}
	.network-viz::before {
		content: '';
		position: absolute;
		inset: 0;
		padding: 1px;
		border-radius: inherit;
		background: linear-gradient(
			135deg,
			rgba(33, 86, 233, 0.6),
			rgba(255, 255, 255, 0.1),
			rgba(33, 86, 233, 0.3)
		);
		-webkit-mask:
			linear-gradient(#000 0 0) content-box,
			linear-gradient(#000 0 0);
		mask:
			linear-gradient(#000 0 0) content-box,
			linear-gradient(#000 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
	}

	.network-lines line {
		animation: dash-flow 20s linear infinite;
	}

	@keyframes dash-flow {
		to {
			stroke-dashoffset: -200;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.network-lines line {
			animation: none;
		}
	}
</style>
